import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeftIcon, ClockIcon, AcademicCapIcon } from '@heroicons/react/24/outline';
import { elearningAPI } from '../../services/elearningApi';
import { Course, Enrollment, CourseDifficulty, EnrollmentStatus } from '../../types/elearning';
import ModuleList from '../../components/elearning/ModuleList';
import EnrollmentButton from '../../components/elearning/EnrollmentButton';
import Breadcrumb from '../../components/layout/Breadcrumb';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import ErrorState from '../../components/ui/ErrorState';
import Badge from '../../components/ui/Badge';
import { useAccessLevel } from '../../hooks/useAccessLevel';
import { AccessLevel } from '../../types/access';
import { usePageTitle } from '../../hooks/usePageTitle';

const difficultyLabels: Record<CourseDifficulty, string> = {
  [CourseDifficulty.BEGINNER]: 'Beginner',
  [CourseDifficulty.INTERMEDIATE]: 'Intermediate',
  [CourseDifficulty.ADVANCED]: 'Advanced',
};

export default function CourseDetail() {
  const { slug } = useParams<{ slug: string }>();
  const [course, setCourse] = useState<Course | null>(null);
  const [catalog, setCatalog] = useState<Course[]>([]);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [enrollment, setEnrollment] = useState<Enrollment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { accessLevel } = useAccessLevel();
  usePageTitle(course?.title);

  useEffect(() => {
    const fetchCourse = async () => {
      if (!slug) {
        setError(new Error('Invalid course slug'));
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const [courseData, courseCatalog, userEnrollments] = await Promise.all([
          elearningAPI.fetchCourse(slug),
          elearningAPI.fetchCourses().catch(() => [] as Course[]),
          elearningAPI.getUserEnrollments().catch(() => [] as Enrollment[]),
        ]);
        if (!courseData) {
          setError(new Error('Course not found'));
          setLoading(false);
          return;
        }

        setCourse(courseData);
        setCatalog(courseCatalog);
        setEnrollments(userEnrollments);
        const userEnrollment = userEnrollments.find((e) => e.courseId === courseData.id);
        setEnrollment(userEnrollment || null);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to load course'));
      } finally {
        setLoading(false);
      }
    };

    fetchCourse();
  }, [slug]);

  const handleEnrollmentChange = (newEnrollment: Enrollment | null) => {
    setEnrollment(newEnrollment);
    setEnrollments((current) => {
      const withoutCourse = current.filter((item) => item.courseId !== course?.id);
      return newEnrollment ? [...withoutCourse, newEnrollment] : withoutCourse;
    });
  };

  const formatTime = (minutes: number) => {
    if (minutes < 60) return `${minutes} minutes`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours} hour${hours > 1 ? 's' : ''} ${mins} minute${mins > 1 ? 's' : ''}` : `${hours} hour${hours > 1 ? 's' : ''}`;
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <LoadingSkeleton lines={10} className="mb-4" />
        <LoadingSkeleton lines={5} />
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ErrorState
          error={error || new Error('Course not found')}
          title="Course Not Found"
          onRetry={() => window.location.reload()}
        />
      </div>
    );
  }

  const prerequisiteCourses = (course.prerequisites || []).map((courseId) => {
    const required = catalog.find((item) => item.id === courseId);
    const completed = enrollments.some(
      (item) => item.courseId === courseId && item.status === EnrollmentStatus.COMPLETED
    );
    return {
      id: courseId,
      title: required?.title || 'Required course',
      slug: required?.slug,
      completed,
    };
  });
  const prerequisitesMet = prerequisiteCourses.every((item) => item.completed);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Breadcrumb
        items={[
          { label: 'Home', to: '/' },
          { label: 'eLearning', to: '/elearning' },
          { label: course.title },
        ]}
      />

      <Link
        to="/elearning"
        className="inline-flex items-center gap-2 text-primary hover:text-primary-dark mb-6 transition-colors"
      >
        <ArrowLeftIcon className="h-5 w-5" />
        <span>Back to Courses</span>
      </Link>

      {/* Course Header */}
      <div className="mb-8">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 mb-3">{course.title}</h1>
            <div className="flex items-center gap-3 mb-4">
              <Badge>{difficultyLabels[course.difficulty]}</Badge>
              {accessLevel === AccessLevel.Admin && (
                <Badge variant="audience">
                  {course.status === 'ARCHIVED'
                    ? 'Archived'
                    : course.status === 'PUBLISHED'
                      ? 'Published'
                      : 'Draft'}
                </Badge>
              )}
            </div>
          </div>
          {accessLevel === AccessLevel.Admin && (
            <Link
              to={`/elearning/admin/courses/${course.id}/edit`}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors font-medium text-sm"
            >
              Edit Course
            </Link>
          )}
        </div>

        {course.description && (
          <p className="text-lg text-gray-700 mb-6">{course.description}</p>
        )}

        {/* Course Metadata */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="flex items-center gap-2 text-gray-600">
            <ClockIcon className="h-5 w-5" />
            <span>{formatTime(course.estimatedTime)}</span>
          </div>
          {course.modules && (
            <div className="flex items-center gap-2 text-gray-600">
              <AcademicCapIcon className="h-5 w-5" />
              <span>{course.modules.length} modules</span>
            </div>
          )}
          {enrollment && (
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-accent">
                {enrollment.progress}% complete
              </span>
              {enrollment.status === EnrollmentStatus.COMPLETED && (
                <Link
                  to="/elearning/certifications"
                  className="text-sm font-medium text-primary hover:text-primary-dark transition-colors"
                >
                  View certificate
                </Link>
              )}
            </div>
          )}
        </div>

        {/* Progress Bar */}
        {enrollment && (
          <div className="mb-6">
            <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary transition-all"
                style={{ width: `${enrollment.progress}%` }}
              />
            </div>
          </div>
        )}

        {/* Enrollment Button */}
        <div className="mb-8">
          <EnrollmentButton
            courseId={course.id}
            courseSlug={course.slug}
            modules={course.modules}
            enrollment={enrollment || undefined}
            startLocked={!prerequisitesMet}
            onEnrollmentChange={handleEnrollmentChange}
          />
        </div>
      </div>

      {/* Learning Objectives */}
      {course.objectives && course.objectives.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Learning Objectives</h2>
          <ul className="list-disc list-inside space-y-2 text-gray-700">
            {course.objectives.map((objective, index) => (
              <li key={index}>{objective}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Prerequisites */}
      {course.prerequisites && course.prerequisites.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Prerequisites</h2>
          <p className="text-gray-600">
            This course requires completion of the following courses:
          </p>
          <ul className="list-disc list-inside space-y-1 text-gray-700 mt-2">
            {prerequisiteCourses.map((required) => (
              <li key={required.id}>
                {required.slug ? (
                  <Link to={`/elearning/courses/${required.slug}`} className="text-primary hover:text-primary-dark">
                    {required.title}
                  </Link>
                ) : (
                  required.title
                )}
                {required.completed ? ' (completed)' : ''}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Modules */}
      {course.modules && course.modules.length > 0 ? (
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Course Modules</h2>
          <ModuleList
            modules={course.modules}
            courseSlug={course.slug}
            enrollment={enrollment || undefined}
            forceLocked={!prerequisitesMet}
          />
        </div>
      ) : (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
          <p className="text-gray-600">No modules available yet.</p>
          {accessLevel === AccessLevel.Admin && (
            <Link
              to={`/elearning/admin/courses/${course.id}/edit`}
              className="mt-4 inline-block text-primary hover:text-primary-dark font-medium"
            >
              Add modules to this course
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

